import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Combobox,
  Group,
  Loader,
  Pill,
  PillsInput,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import { StyledSearchableMultiInput } from "./SearchableMultiInput.styled";


interface OptionItem {
  label: string;
  value: string;
}

interface SearchableMultiInputProps {
  label: string;
  placeholder?: string;
  value: OptionItem[];
  onChange: (value: OptionItem[]) => void;
  error?: string;
  withAsterisk?: boolean;
  disabled?: boolean;
  required?: boolean;
  
  // Data source
  data: OptionItem[];
  isLoading?: boolean;
  
  // Create new functionality (optional)
  onCreateNew?: (searchTerm: string) => Promise<OptionItem> | void;
  isCreating?: boolean;
  createButtonText?: string;
  
  // Customizable text
  emptyStateText?: string;
  loadingText?: string;
  
  // Additional props
  maxHeight?: number;
  searchable?: boolean;
}

export const SearchableMultiInput = ({
  label,
  placeholder = "Search or type to create...",
  value,
  onChange,
  error,
  withAsterisk = false,
  disabled = false,
  required = false,
  data = [],
  isLoading = false,
  onCreateNew,
  isCreating = false,
  createButtonText = "Create",
  emptyStateText = "No options found",
  loadingText = "Loading...",
  maxHeight = 200,
  searchable = true,
}: SearchableMultiInputProps) => {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const [search, setSearch] = useState("");

  const handleValueSelect = (val: string) => {
    // Find the option
    const selectedOption = data.find(option => option.value === val);
    if (selectedOption) {
      const isAlreadySelected = value.some(item => item.value === val);
      if (!isAlreadySelected) {
        onChange([...value, selectedOption]);
      }
    }
    setSearch("");
    combobox.closeDropdown();
  };

  const handleValueRemove = (val: string) => {
    onChange(value.filter(item => item.value !== val));
  };

  const handleCreateNew = async () => {
    if (search.trim() && onCreateNew) {
      try {
        console.log("Before create - current values:", value);
        const result = onCreateNew(search.trim());
        // Handle both Promise and direct return
        const newOption = result instanceof Promise ? await result : result;
        console.log("New option created:", newOption);
        if (newOption) {
          const updatedValues = [...value, newOption];
          console.log("Updated values:", updatedValues);
          onChange(updatedValues);
          setSearch("");
          combobox.closeDropdown();
        }
      } catch (error) {
        console.error("Error creating new option:", error);
      }
    }
  };

  // Filter options based on search
  const filteredOptions = searchable 
    ? data.filter(option =>
        option.label.toLowerCase().includes(search.toLowerCase().trim()) &&
        !value.some(selected => selected.value === option.value)
      )
    : data.filter(option => !value.some(selected => selected.value === option.value));

  // Check if search term matches any existing option exactly
  const exactMatch = data.some(option =>
    option.label.toLowerCase() === search.toLowerCase().trim()
  );

  // Show create option if there's a search term, no exact match, and create function is provided
  const showCreateOption = searchable && search.trim() && !exactMatch && onCreateNew;

  const values = value.map((item) => (
    <Pill 
      key={item.value} 
      withRemoveButton 
      onRemove={() => handleValueRemove(item.value)}
      disabled={disabled}
    >
      {item.label}
    </Pill>
  ));

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item.value} key={item.value}>
      <Group gap="sm">
        <span>{item.label}</span>
      </Group>
    </Combobox.Option>
  ));

  return (
    <StyledSearchableMultiInput>
      <Combobox
        store={combobox}
        onOptionSubmit={handleValueSelect}
        disabled={disabled}
      >
      <Combobox.DropdownTarget>
        <PillsInput
          label={label}
          error={error}
          withAsterisk={withAsterisk}
          required={required}
          disabled={disabled}
          onClick={() => !disabled && combobox.openDropdown()}
        >
          <Pill.Group>
            {values}
            {searchable && (
              <Combobox.EventsTarget>
                <PillsInput.Field
                  placeholder={value.length === 0 ? placeholder : ""}
                  value={search}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                    combobox.openDropdown();
                  }}
                  onFocus={() => !disabled && combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Backspace" && search.length === 0 && value.length > 0) {
                      event.preventDefault();
                      handleValueRemove(value[value.length - 1].value);
                    }
                  }}
                  disabled={disabled}
                />
              </Combobox.EventsTarget>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options style={{ maxHeight, overflowY: 'auto' }}>
          {isLoading ? (
            <Combobox.Option value="" disabled>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">{loadingText}</Text>
              </Group>
            </Combobox.Option>
          ) : (
            <>
              {options}
              {showCreateOption && (
                <Combobox.Option value="">
                  <Button
                    variant="filled"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleCreateNew}
                    loading={isCreating}
                    fullWidth
                    style={{ justifyContent: "flex-start" }}
                  >
                    {createButtonText}: &ldquo;{search}&rdquo;
                  </Button>
                </Combobox.Option>
              )}
              {options.length === 0 && !showCreateOption && (
                <Combobox.Empty>{emptyStateText}</Combobox.Empty>
              )}
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
    </StyledSearchableMultiInput>
  );
};
